/**
 * Placement wiring next to the globe: turns the engine's placement mode on and
 * off with the store, routes ground clicks to the dialog, keeps the ghost's
 * preview in step with the pending spec, handles Esc, and shows the hint bar
 * and the dialog. LoaderGlobe imports Cesium lazily, so the engine calls wait
 * for the awareness store's engineMounted flag (set once the engine is there).
 */
import { useEffect, useMemo, useState } from 'react';
import { MapPinPlus, X } from 'lucide-react';
import { useUI } from '@/state/store';
import { toast } from '@/components/ui/toast';
import { useAwareness } from '../awareness/store';
import { getGlobeEngine } from '../globe/engineHandle';
import { formatDiameterM } from '../globe/placementPreview';
import { useLoader } from '../store';
import { PlacementDialog } from './PlacementDialog';
import { formatLatLon, previewAltitudeKm } from './placement';
import { usePlacement, type PlacementTarget } from './store';
import { syncPlacedTwins } from './twinBridge';
import { usePlaceAvailable } from './usePlaceAvailable';

export function PlacementLayer() {
  const available = usePlaceAvailable();
  const presenter = useUI((s) => s.presenter);
  const active = usePlacement((s) => s.active);
  const target = usePlacement((s) => s.target);
  const diameterM = usePlacement((s) => s.pending.diameterM);
  const elevationMinDeg = usePlacement((s) => s.pending.elevationMinDeg);
  const bands = usePlacement((s) => s.pending.bands);
  const dataset = useLoader((s) => s.dataset);
  const engineMounted = useAwareness((s) => s.engineMounted);
  const allowed = available && !presenter;

  // Placed ids and their twin specs follow the dataset (network switch, D1 sync); no dataset ends placement.
  useEffect(() => {
    usePlacement.getState().reconcile(dataset ? dataset.antennas.map((a) => a.id) : null);
    try {
      syncPlacedTwins(dataset ? dataset.antennas : null);
    } catch (err) {
      console.error('[place] registering placed-antenna twins failed:', err);
      toast(
        'error',
        `Placed antenna twins could not be registered: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }, [dataset]);

  // Operations, the slim profile and presenter mode have no placement.
  useEffect(() => {
    if (!allowed) usePlacement.getState().cancel();
  }, [allowed]);

  // The globe stage going away (a non-globe mode) ends placement.
  useEffect(() => () => usePlacement.getState().cancel(), []);

  // Engine placement mode follows the store; a ground click opens the dialog.
  useEffect(() => {
    if (!active || !engineMounted) return;
    const engine = getGlobeEngine();
    if (!engine) {
      console.error(
        '[place] the awareness store reports a mounted globe engine but getGlobeEngine() is null',
      );
      toast('error', 'The globe is not ready, so placement was cancelled.');
      usePlacement.getState().cancel();
      return;
    }
    const offClick = engine.on('groundClick', (lat, lon) => usePlacement.getState().setTarget(lat, lon));
    engine.setPlacementMode(true);
    return () => {
      offClick();
      engine.setPlacementMode(false);
    };
  }, [active, engineMounted]);

  // The ghost ring previews the coverage circle the antenna will get (see previewAltitudeKm).
  const altKm = useMemo(() => previewAltitudeKm(bands, dataset?.missions ?? []), [bands, dataset]);
  useEffect(() => {
    if (!active || !engineMounted) return;
    getGlobeEngine()?.setPlacementPreview({ diameterM, elevationMinDeg, altKm });
  }, [active, engineMounted, diameterM, elevationMinDeg, altKm]);

  // Esc cancels placement while waiting for the click. It stops there, so the shell's
  // Esc (clear selection) and the globe's (stop following, reset view) do not also run.
  // An open dialog handles its own Esc.
  useEffect(() => {
    if (!active || target) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (e.target instanceof Element && e.target.closest('[role="dialog"]')) return;
      e.preventDefault();
      e.stopPropagation();
      usePlacement.getState().cancel();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [active, target]);

  // Where the last press was released while waiting for the click (capture phase, so before the
  // globe turns it into a ground click): the dialog opens beside that point, leaving the ghost visible.
  const [anchorX, setAnchorX] = useState<number | null>(null);
  useEffect(() => {
    if (!active || target) return;
    const onUp = (e: PointerEvent) => setAnchorX(e.clientX);
    window.addEventListener('pointerup', onUp, true);
    return () => window.removeEventListener('pointerup', onUp, true);
  }, [active, target]);

  return (
    <>
      {active && !target && engineMounted && <PlacementHint diameterM={diameterM} />}
      <PlacementDialog anchorX={anchorX} />
    </>
  );
}

/** "Click the globe to place a 13 m antenna, Esc to cancel" with the coordinates under the pointer. */
function PlacementHint({ diameterM }: { diameterM: number }) {
  const [hover, setHover] = useState<PlacementTarget | null>(null);
  useEffect(() => {
    const engine = getGlobeEngine();
    if (!engine) return undefined;
    return engine.on('groundHover', (lat, lon) => setHover({ lat, lon }));
  }, []);
  return (
    <div className="pointer-events-none absolute inset-x-0 top-14 flex justify-center px-3">
      <div
        role="status"
        className="pointer-events-auto flex items-center gap-3 rounded-full border border-accent/40 bg-raised/90 px-3 py-1 text-xs text-ink backdrop-blur"
      >
        <MapPinPlus className="h-3.5 w-3.5 shrink-0 text-accent" />
        <span>Click the globe to place a {formatDiameterM(diameterM)} m antenna, Esc to cancel</span>
        {hover && <span className="font-mono text-ink-2">{formatLatLon(hover.lat, hover.lon)}</span>}
        <button
          type="button"
          aria-label="Cancel placement"
          onClick={() => usePlacement.getState().cancel()}
          className="text-ink-3 hover:text-ink"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
